import React from 'react';
import { observer } from 'mobx-react';
import { Formik, useFormikContext, getIn, setIn } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import { lookupsJson } from '@ourbranch/lookups';
import map from 'lodash/map';
import { FarmScheduleCoverageType } from '@ourbranch/lookups';

import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import Field from 'core/components/form/form.v2';
import { AppliedTag } from 'common/components/farm/applied-tag';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import Grid from '@material-ui/core/Grid';
import CollapsibleListItem from './collapsible-list-item';
import alertIcon from 'core/assets/svg/alert-bold-on-card.svg';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import {
  ValidationErrorsModal,
  useValidationErrorsModal,
  setTouchedFromErrors
} from 'core/components/validation-errors-modal';
import useStyles from './sub-risk.styles';
import { hasErrorMessages } from './sub-risk.utils';
import AddFormContainer from 'common/components/farm/add-form-container';

export const validationSchema = (variant) =>
  Yup.object().shape({
    category: Yup.string().required('Type of item is required'),
    limit: Yup.number()
      .required('Limit is required')
      .min(5000, 'Limit minimum is $5,000')
      .max(25000, 'Limit maximum is $25,000'),
    coinsurance: Yup.string().required('Coinsurance is required'),
    days: Yup.string().nullable(),
    description: Yup.string().nullable(),
    linkedItemId: variant === 'F' ? Yup.string().required('Item is required') : Yup.string().nullable()
  });

// Variant E allows one item per type; F and G only allow the type with id '1'.
const getAvailableTypeOptions = (variant, usedCategories) => {
  const { farmIncomeItemTypes } = lookupsJson.farm;
  if (variant === 'E') {
    return farmIncomeItemTypes.filter((t) => !usedCategories.has(t.id));
  }
  if (variant === 'F' || variant === 'G') {
    return farmIncomeItemTypes.filter((t) => t.id === '1');
  }
  return farmIncomeItemTypes;
};

const FarmIncomeCoverageForm = ({ fieldName, disabled, variant, linkSourceFieldName }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched, validateForm, setErrors, setTouched } = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();
  const { inPolicyRoute } = useRouteHelper();

  const scheduleCoverages = getIn(values, fieldName) || [];
  const farmIncomeItems =
    scheduleCoverages.find((item) => item.coverageType === FarmScheduleCoverageType.INCOME)?.items || [];
  const coverageIndex = scheduleCoverages.findIndex((c) => c.coverageType === FarmScheduleCoverageType.INCOME);

  // F: items to link to live in coverages.coverageF (passed via linkSourceFieldName).
  // Only items with an id have been processed by the rater; unrated items aren't selectable yet.
  const linkSourceItems = variant === 'F' && linkSourceFieldName ? getIn(values, linkSourceFieldName) || [] : [];
  const ratedLinkItems = linkSourceItems.filter((item) => item.id);
  const linkedIds = new Set(farmIncomeItems.map((i) => i.linkedItemId).filter(Boolean));
  const availableLinkItems = ratedLinkItems.filter((item) => !linkedIds.has(item.id));
  const linkDropdownOptions = availableLinkItems.map((item) => {
    // "description - type" label; fall back to bare description if the category doesn't resolve in the lookup
    const typeLabel = lookupsJson.farm.scheduledFarmPropertyCategories.find((t) => t.id === item.category)?.value;
    return { id: item.id, value: typeLabel ? `${item.description} - ${typeLabel}` : item.description };
  });

  const usedCategories = new Set(farmIncomeItems.map((i) => i.category));
  const availableTypeOptions = getAvailableTypeOptions(variant, usedCategories);
  const showOnePerTypeDisclaimer = variant === 'E' && availableTypeOptions.length === 0;
  const hideAddFormForF = variant === 'F' && availableLinkItems.length === 0;
  const hideAddFormForG = variant === 'G' && farmIncomeItems.length >= 1;

  // Auto-expand if there are any scheduled farm income items
  const hasScheduledItems = farmIncomeItems.length > 0;

  return (
    <>
      <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Farm Income Coverage</div>}
        contentRight={hasScheduledItems ? <AppliedTag /> : null}
        initialOpen={hasScheduledItems}
      >
        <FormSectionWrapper
          sectionTitle="Farm income coverage items"
          totalLabel="items"
          totalCount={farmIncomeItems?.length || 0}
          emptyStateTitle="There are no farm income items added yet"
        >
          {/* list items */}
          {map(farmIncomeItems, (item, index) => {
            // Fall back to the raw category id if no lookup match — guards against legacy / out-of-sync data crashing the section
            const itemCategory =
              lookupsJson.farm.farmIncomeItemTypes.find((type) => type.id === item.category)?.value ?? item.category;
            const linkedItem = variant === 'F' ? linkSourceItems.find((s) => s.id === item.linkedItemId) : null;
            const rowTitle =
              variant === 'F' ? `${itemCategory} - ${linkedItem?.description ?? 'Unlinked'}` : itemCategory;
            const itemPath = `${fieldName}[${coverageIndex}].items[${index}]`;

            return (
              <CollapsibleListItem
                key={`farmIncome-${index}`}
                title={rowTitle}
                rightSideValues={[{ label: 'Limit', value: `$${item.limit?.toLocaleString('en-US') || item.limit}` }]}
                onDelete={() => {
                  const newItems = farmIncomeItems.filter((_, checkIndex) => checkIndex !== index);
                  const currentScheduleCoverages =
                    getIn(values, fieldName)?.filter((item) => item.coverageType !== FarmScheduleCoverageType.INCOME) ||
                    [];
                  setFieldValue(`${fieldName}`, [
                    ...currentScheduleCoverages,
                    {
                      coverageType: FarmScheduleCoverageType.INCOME,
                      items: newItems
                    }
                  ]);
                  setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.INCOME}`);
                }}
                disabled={disabled}
              >
                <Field
                  xs={6}
                  type="numeric"
                  id={`${itemPath}.limit`}
                  name={`${itemPath}.limit`}
                  mode="dark"
                  label="Limit"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled={disabled}
                />
                <Field
                  xs={6}
                  type="select"
                  id={`${itemPath}.extendedDetails.coinsurance`}
                  name={`${itemPath}.extendedDetails.coinsurance`}
                  mode="dark"
                  label="Coinsurance"
                  options={lookupsJson.farm.farmIncomeCoinsurance}
                  disabled={disabled}
                />
                <Field
                  xs={6}
                  type="select"
                  id={`${itemPath}.extendedDetails.days`}
                  name={`${itemPath}.extendedDetails.days`}
                  mode="dark"
                  label="Period of loss extension (in days)"
                  options={lookupsJson.farm.farmIncomeLossExtensionDays}
                  disabled={disabled}
                />
                <Field
                  xs={12}
                  type="textarea"
                  id={`${itemPath}.description`}
                  name={`${itemPath}.description`}
                  mode="dark"
                  label="Description"
                  disabled={disabled}
                />
              </CollapsibleListItem>
            );
          })}

          {/* add form */}
          {!hideAddFormForG && (
            <AddFormContainer className={classes.addFormContainer}>
              {showOnePerTypeDisclaimer ? (
                <div className={classes.alert}>
                  <img src={alertIcon} className={classes.alertIconInline} alt="Alert Icon" />
                  <div className={classes.alertMessage}>
                    You may only add a single farm income coverage for each type
                  </div>
                </div>
              ) : null}
              {!showOnePerTypeDisclaimer && hideAddFormForF && (
                <div className={classes.alert}>
                  <img src={alertIcon} className={classes.alertIconInline} alt="Alert Icon" />
                  <div className={classes.alertMessageFull}>
                    You may only add a single farm income coverage for each scheduled item. If you add another scheduled
                    item, {inPolicyRoute ? 'preview changes' : 'please update the offer'} to apply farm income coverage.
                  </div>
                </div>
              )}
              {!showOnePerTypeDisclaimer && !hideAddFormForF && (
                <Formik
                  initialValues={{
                    category: variant === 'F' || variant === 'G' ? '1' : undefined,
                    limit: undefined,
                    coinsurance: undefined,
                    days: undefined,
                    description: undefined,
                    linkedItemId: undefined
                  }}
                  validateOnChange={false}
                  validateOnBlur={false}
                  validationSchema={validationSchema(variant)}
                  onSubmit={async (val, { resetForm }) => {
                    const parentErrors = await validateForm();
                    // Exclude scheduleCoverages error since adding this item is what resolves it (GitHub issue #29426)
                    const errorsExcludingScheduleCoverages = setIn(parentErrors, fieldName, undefined);
                    if (hasErrorMessages(errorsExcludingScheduleCoverages)) {
                      setErrors(parentErrors);
                      setTouched(setTouchedFromErrors(parentErrors), false);
                      setShowValidationErrorsModal(true);
                      return;
                    }

                    const item = val;
                    Object.keys(item).forEach((key) => {
                      if (item[key] === '') {
                        item[key] = null;
                      }
                    });

                    const newItem = {
                      category: item.category,
                      description: item.description,
                      limit: item.limit,
                      extendedDetails: {
                        coinsurance: item.coinsurance,
                        days: item.days
                      },
                      ...(variant === 'F' ? { linkedItemId: item.linkedItemId } : {})
                    };

                    const currentScheduleCoverages =
                      getIn(values, fieldName)?.filter(
                        (item) => item.coverageType !== FarmScheduleCoverageType.INCOME
                      ) || [];
                    const currentFarmIncomeItems =
                      getIn(values, fieldName)?.find((item) => item.coverageType === FarmScheduleCoverageType.INCOME)
                        ?.items || [];

                    setFieldValue(`${fieldName}`, [
                      ...currentScheduleCoverages,
                      {
                        coverageType: FarmScheduleCoverageType.INCOME,
                        items: [...currentFarmIncomeItems, newItem]
                      }
                    ]);
                    setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.INCOME}`);

                    resetForm();
                  }}
                >
                  {({ submitForm }) => {
                    return (
                      <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                        {variant === 'F' && (
                          <Field
                            xs={12}
                            type="select"
                            id="linkedItemId"
                            name="linkedItemId"
                            mode="dark"
                            label="Item"
                            options={linkDropdownOptions}
                            disabled={disabled}
                          />
                        )}
                        <Field
                          xs={6}
                          type="select"
                          id="category"
                          name="category"
                          mode="dark"
                          label="Type of item"
                          options={availableTypeOptions}
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="numeric"
                          id="limit"
                          name="limit"
                          mode="dark"
                          label="Limit"
                          format={{ thousandSeparator: true, prefix: '$' }}
                          disabled={disabled}
                        />

                        <Field
                          xs={6}
                          type="select"
                          id="coinsurance"
                          name="coinsurance"
                          mode="dark"
                          label="Coinsurance"
                          options={lookupsJson.farm.farmIncomeCoinsurance}
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="select"
                          id="days"
                          name="days"
                          mode="dark"
                          label="Period of loss extension (in days)"
                          options={lookupsJson.farm.farmIncomeLossExtensionDays}
                          disabled={disabled}
                        />

                        <Field
                          xs={12}
                          type="textarea"
                          id="description"
                          name="description"
                          mode="dark"
                          label="Description"
                          disabled={disabled}
                        />

                        <Grid container alignItems="center" className={classes.singleBottom}>
                          <Button
                            type="button"
                            mode="big"
                            variant="contained"
                            color="secondary"
                            className={classes.button}
                            disabled={disabled}
                            onClick={submitForm}
                          >
                            Add Item
                          </Button>
                        </Grid>
                      </Grid>
                    );
                  }}
                </Formik>
              )}
            </AddFormContainer>
          )}
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </>
  );
};

FarmIncomeCoverageForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(['E', 'F', 'G']).isRequired,
  linkSourceFieldName: (props, propName, componentName) => {
    const value = props[propName];
    if (props.variant === 'F' && (value == null || value === '')) {
      return new Error(
        `Invalid prop \`${propName}\` supplied to \`${componentName}\`: required when \`variant\` is 'F'.`
      );
    }
    if (value != null && typeof value !== 'string') {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof value}\` supplied to \`${componentName}\`, expected \`string\`.`
      );
    }
    return null;
  }
};

FarmIncomeCoverageForm.defaultProps = {
  disabled: false,
  linkSourceFieldName: undefined
};

export default observer(FarmIncomeCoverageForm);
